import clsx from 'clsx'
import { forwardRef, ReactNode } from 'react'

export type TagSize = 'sm' | 'md' | 'lg'

export interface TagProps {
    size?: TagSize
    children: ReactNode
    checkbox?: boolean
    checked?: boolean
    onCheckedChange?: (checked: boolean) => void
    dot?: boolean
    flag?: ReactNode
    avatar?: ReactNode
    onClose?: () => void
    count?: number
    disabled?: boolean
    className?: string
}

interface SizeClasses {
    text: string
    paddingY: string
    paddingLeft: string
    paddingLeftLeading: string
    paddingRight: string
    paddingRightClose: string
    paddingRightCount: string
    contentGap: string
    checkbox: string
    checkGlyph: string
    closeButton: string
    closeIcon: string
    closeStroke: number
    count: string
}

const SIZE_CLASSES: Record<TagSize, SizeClasses> = {
    sm: {
        text: 'text-xs',
        paddingY: 'py-[3px]',
        paddingLeft: 'pl-spacing-md',
        paddingLeftLeading: 'pl-[5px]',
        paddingRight: 'pr-spacing-md',
        paddingRightClose: 'pr-spacing-xs',
        paddingRightCount: 'pr-spacing-xs',
        contentGap: 'gap-spacing-xs',
        checkbox: 'size-3.5',
        checkGlyph: 'size-2.5',
        closeButton: 'p-spacing-xxs',
        closeIcon: 'size-2.5',
        closeStroke: 3.6,
        count: 'h-4 px-spacing-xs text-xs',
    },
    md: {
        text: 'text-sm',
        paddingY: 'py-spacing-xxs',
        paddingLeft: 'pl-[9px]',
        paddingLeftLeading: 'pl-spacing-xs',
        paddingRight: 'pr-[9px]',
        paddingRightClose: 'pr-spacing-xs',
        paddingRightCount: 'pr-[3px]',
        contentGap: 'gap-[5px]',
        checkbox: 'size-4',
        checkGlyph: 'size-3',
        closeButton: 'p-spacing-xxs',
        closeIcon: 'size-3',
        closeStroke: 3,
        count: 'h-[18px] px-[5px] text-xs',
    },
    lg: {
        text: 'text-sm',
        paddingY: 'py-spacing-xs',
        paddingLeft: 'pl-2.5',
        paddingLeftLeading: 'pl-[5px]',
        paddingRight: 'pr-2.5',
        paddingRightClose: 'pr-spacing-xs',
        paddingRightCount: 'pr-spacing-xs',
        contentGap: 'gap-spacing-sm',
        checkbox: 'size-[18px]',
        checkGlyph: 'size-3.5',
        closeButton: 'p-[3px]',
        closeIcon: 'size-3.5',
        closeStroke: 2.57,
        count: 'h-[18px] px-spacing-sm text-sm',
    },
}

export const Tag = forwardRef<HTMLSpanElement, TagProps>(
    (
        {
            size = 'sm',
            children,
            checkbox = false,
            checked = false,
            onCheckedChange,
            dot = false,
            flag,
            avatar,
            onClose,
            count,
            disabled = false,
            className,
        },
        ref,
    ) => {
        const sizeClasses = SIZE_CLASSES[size]
        const hasCount = count !== undefined
        const hasLeading = checkbox || dot || Boolean(flag) || Boolean(avatar)

        const getPaddingRight = () => {
            if (onClose) {
                return sizeClasses.paddingRightClose
            }
            if (hasCount) {
                return sizeClasses.paddingRightCount
            }
            return sizeClasses.paddingRight
        }

        const rootClasses = clsx(
            'inline-flex items-center gap-[3px] font-body font-medium text-text-secondary',
            'rounded-radius-sm border border-solid border-border-primary bg-background-bg-primary',
            sizeClasses.text,
            sizeClasses.paddingY,
            hasLeading ? sizeClasses.paddingLeftLeading : sizeClasses.paddingLeft,
            getPaddingRight(),
            disabled && 'cursor-not-allowed opacity-50',
            className,
        )

        const renderLeading = () => {
            if (checkbox) {
                return (
                    <span
                        aria-hidden="true"
                        className={clsx(
                            'inline-flex shrink-0 items-center justify-center rounded-radius-xs border border-solid',
                            sizeClasses.checkbox,
                            checked
                                ? 'border-border-brand bg-background-bg-brand-solid text-foreground-fg-white'
                                : 'border-border-primary bg-background-bg-primary',
                        )}>
                        {checked && (
                            <svg className={sizeClasses.checkGlyph} viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M20 6 9 17l-5-5"
                                    stroke="currentColor"
                                    strokeWidth={3.33}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        )}
                    </span>
                )
            }
            if (dot) {
                return (
                    <span
                        aria-hidden="true"
                        className="size-1.5 shrink-0 rounded-radius-full bg-foreground-fg-success-secondary"
                    />
                )
            }
            if (flag) {
                return (
                    <span className="size-4 shrink-0 overflow-hidden rounded-radius-full">
                        {flag}
                    </span>
                )
            }
            if (avatar) {
                return (
                    <span className="size-4 shrink-0 overflow-hidden rounded-radius-full">
                        {avatar}
                    </span>
                )
            }
            return null
        }

        const countBadge = hasCount && (
            <span
                className={clsx(
                    'inline-flex shrink-0 items-center justify-center rounded-[3px] bg-background-bg-tertiary font-medium text-text-secondary',
                    sizeClasses.count,
                )}>
                {count}
            </span>
        )

        const contentChildren = (
            <>
                {renderLeading()}
                <span>{children}</span>
                {countBadge}
            </>
        )

        const handleToggle = () => {
            if (disabled) {
                return
            }
            onCheckedChange?.(!checked)
        }

        const content = checkbox ? (
            <button
                type="button"
                role="checkbox"
                aria-checked={checked}
                disabled={disabled}
                onClick={handleToggle}
                className={clsx(
                    'inline-flex items-center border-0 bg-base-transparent p-spacing-none font-medium text-text-secondary',
                    'rounded-radius-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
                    sizeClasses.contentGap,
                    sizeClasses.text,
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                )}>
                {contentChildren}
            </button>
        ) : (
            <span className={clsx('inline-flex items-center', sizeClasses.contentGap)}>
                {contentChildren}
            </span>
        )

        return (
            <span ref={ref} className={rootClasses}>
                {content}
                {onClose && (
                    <button
                        type="button"
                        aria-label="Remove"
                        onClick={onClose}
                        disabled={disabled}
                        className={clsx(
                            'inline-flex shrink-0 items-center justify-center rounded-[3px] border-0 bg-base-transparent',
                            'text-foreground-fg-quaternary transition-colors',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
                            disabled
                                ? 'cursor-not-allowed'
                                : 'cursor-pointer hover:bg-background-bg-tertiary hover:text-foreground-fg-quaternary_hover',
                            sizeClasses.closeButton,
                        )}>
                        <svg
                            className={sizeClasses.closeIcon}
                            viewBox="0 0 24 24"
                            fill="none"
                            aria-hidden="true">
                            <path
                                d="M18 6 6 18M6 6l12 12"
                                stroke="currentColor"
                                strokeWidth={sizeClasses.closeStroke}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                )}
            </span>
        )
    },
)

Tag.displayName = 'Tag'
